/**
 * Preview-only metadata store for custom fields.
 *
 * The `preview-custom-fields` registry stores id/label/name only. This module
 * layers additional per-field metadata (type, description, options, AI prompt)
 * on parallel localStorage keys so the Data Model settings view and the grid
 * Manage Columns dialog can share the same source of truth in preview mode.
 */

import type { PreviewCustomObjectType } from "@/lib/preview-custom-fields";

const KEYS = {
  type: "data-model-field-types",
  description: "data-model-field-descriptions",
  options: "data-model-field-options",
  aiPrompt: "data-model-field-ai-prompts",
  formula: "data-model-field-formulas",
} as const;

export interface CustomFieldMeta {
  type?: string;
  description?: string;
  options?: string[];
  aiPrompt?: string;
  formula?: string;
}

function keyFor(kind: keyof typeof KEYS, workspaceId: string | null, ot: PreviewCustomObjectType) {
  return `ws_${workspaceId || "default"}__${KEYS[kind]}:${ot}`;
}

function readMap<T>(kind: keyof typeof KEYS, workspaceId: string | null, ot: PreviewCustomObjectType): Record<string, T> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(keyFor(kind, workspaceId, ot));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed as Record<string, T> : {};
  } catch {
    return {};
  }
}

function writeMap<T>(kind: keyof typeof KEYS, workspaceId: string | null, ot: PreviewCustomObjectType, map: Record<string, T>) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(keyFor(kind, workspaceId, ot), JSON.stringify(map));
  } catch { /* ignore quota */ }
}

export function readCustomFieldMeta(
  workspaceId: string | null,
  objectType: PreviewCustomObjectType,
  id: string,
): CustomFieldMeta {
  const type = readMap<string>("type", workspaceId, objectType)[id];
  const description = readMap<string>("description", workspaceId, objectType)[id];
  const options = readMap<string[]>("options", workspaceId, objectType)[id];
  const aiPrompt = readMap<string>("aiPrompt", workspaceId, objectType)[id];
  const formula = readMap<string>("formula", workspaceId, objectType)[id];
  return {
    ...(type ? { type } : {}),
    ...(description ? { description } : {}),
    ...(Array.isArray(options) ? { options } : {}),
    ...(aiPrompt ? { aiPrompt } : {}),
    ...(formula ? { formula } : {}),
  };
}

export function writeCustomFieldMeta(
  workspaceId: string | null,
  objectType: PreviewCustomObjectType,
  id: string,
  meta: CustomFieldMeta,
) {
  if (meta.type !== undefined) {
    const m = readMap<string>("type", workspaceId, objectType);
    m[id] = meta.type;
    writeMap("type", workspaceId, objectType, m);
  }
  {
    const m = readMap<string>("description", workspaceId, objectType);
    if (meta.description && meta.description.trim()) m[id] = meta.description.trim();
    else if (meta.description !== undefined) delete m[id];
    writeMap("description", workspaceId, objectType, m);
  }
  {
    const m = readMap<string[]>("options", workspaceId, objectType);
    if (Array.isArray(meta.options) && meta.options.length > 0) m[id] = meta.options;
    else if (meta.options !== undefined) delete m[id];
    writeMap("options", workspaceId, objectType, m);
  }
  {
    const m = readMap<string>("aiPrompt", workspaceId, objectType);
    if (meta.aiPrompt && meta.aiPrompt.trim()) m[id] = meta.aiPrompt.trim();
    else if (meta.aiPrompt !== undefined) delete m[id];
    writeMap("aiPrompt", workspaceId, objectType, m);
  }
  {
    const m = readMap<string>("formula", workspaceId, objectType);
    if (meta.formula && meta.formula.trim()) m[id] = meta.formula.trim();
    else if (meta.formula !== undefined) delete m[id];
    writeMap("formula", workspaceId, objectType, m);
  }
}

export function deleteCustomFieldMeta(
  workspaceId: string | null,
  objectType: PreviewCustomObjectType,
  id: string,
) {
  (Object.keys(KEYS) as (keyof typeof KEYS)[]).forEach(k => {
    const m = readMap<unknown>(k, workspaceId, objectType);
    if (id in m) {
      delete m[id];
      writeMap(k, workspaceId, objectType, m);
    }
  });
}